import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {GetRenewalEligibleResponse} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface LicenseRenewalContentsProps {
  isAgree: boolean;
  vin: string;
}

function LicenseRenewalContents({isAgree, vin}: LicenseRenewalContentsProps) {
  const {t} = useInferableTranslation();
  return isAgree ? (
    <ErrorBoundary fallback={<></>}>
      <Suspense
        fallback={
          <li className="application_item skeleton">
            <div className="title_area">
              <strong className="title">{t('T10222')}</strong>
            </div>
            <p
              className="desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T10283')}
            </p>
            <div className="btn_box">
              <button
                type="button"
                className="btn btn_large btn_filled btn_full"
              >
                {t('T10284')}
              </button>
            </div>
          </li>
        }
      >
        <AgreeInner />
      </Suspense>
    </ErrorBoundary>
  ) : (
    <li className="application_item">
      <LinkTo
        className="link"
        to={ROUTE_PATHS.PUBLIC_SERVICE_LICENSE_RENEWAL_ABOUT.getFullPath(vin)}
        onClickBypass={() => {
          emitEventLog({
            event: 'public_service_interaction',
            data_action: 'public service button click',
            data_name: '운전면허 적성검사·갱신기간 조회',
          });
        }}
      >
        <div className="application_text">
          <strong className="title">{t('T10212')}</strong>
          <p className="desc">{t('T10213')}</p>
        </div>
        <span className="ic ic_arrow_forward_16px" />
      </LinkTo>
    </li>
  );
}

function AgreeInner() {
  const {t} = useInferableTranslation();
  const {getExternalVariable} = useFirebaseStore(({getExternalVariable}) => ({getExternalVariable}));
  const {data: licenseRenewalData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.licenseRenewalController.getLicenseRenewal)],
    queryFn: () => diApi.licenseRenewalController.getLicenseRenewal({}),
    select: ({data}) => data,
  });

  function goToLicenseRenewal() {
    WebviewUtils.openWebBrowser(getExternalVariable('LICENSE_RENEWAL_URL'));
  }

  return (
    <>
      {(() => {
        switch (licenseRenewalData.renewalType) {
          case GetRenewalEligibleResponse.renewalType.CLASS1_RENEWAL:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">
                    {t('T10222')}

                    <div className="badge_box">
                      <span className="badge badge_basic_container">{t('T10282')}</span>
                    </div>
                  </strong>
                </div>
                <p
                  className="desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T10283')}
                </p>
                <div className="btn_box">
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full"
                    onClick={() => {
                      emitEventLog({
                        event: 'public_service_interaction',
                        data_action: 'public service button click',
                        data_name: '적성검사 신청하러 가기',
                      });
                      goToLicenseRenewal();
                    }}
                  >
                    {t('T10284')}
                  </button>
                </div>
              </li>
            );
          case GetRenewalEligibleResponse.renewalType.CLASS2_RENEWAL:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">
                    {t('T10222')}
                    <div className="badge_box">
                      <span className="badge badge_basic_container">{t('T10285')}</span>
                    </div>
                  </strong>
                </div>
                <p
                  className="desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T10286')}
                </p>
                <div className="btn_box">
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full"
                    onClick={() => {
                      emitEventLog({
                        event: 'public_service_interaction',
                        data_action: 'public service button click',
                        data_name: '면허 갱신하러 가기',
                      });
                      goToLicenseRenewal();
                    }}
                  >
                    {t('T10224')}
                  </button>
                </div>
              </li>
            );
          case GetRenewalEligibleResponse.renewalType.OVER_70YEARS_RENEWAL:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">
                    {t('T10222')}
                    <div className="badge_box">
                      <span className="badge badge_container_gray">{t('T10287')}</span>
                    </div>
                  </strong>
                </div>
                <p
                  className="desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T10288')}
                </p>
                <div className="btn_box">
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full"
                    onClick={() => {
                      emitEventLog({
                        event: 'public_service_interaction',
                        data_action: 'public service button click',
                        data_name: '적성검사 신청하러 가기',
                      });
                      goToLicenseRenewal();
                    }}
                  >
                    {t('T10284')}
                  </button>
                </div>
              </li>
            );
          case GetRenewalEligibleResponse.renewalType.ACCIDENT_FREE_OVER_7YEARS:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">
                    {t('T10222')}
                    <div className="badge_box">
                      <span className="badge badge_basic_container">{t('T10285')}</span>
                      <span className="badge badge_container_gray">{t('T10289')}</span>
                    </div>
                  </strong>
                </div>
                <p
                  className="desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T10290')}
                </p>
                <div className="btn_box">
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full"
                    onClick={() => {
                      emitEventLog({
                        event: 'public_service_interaction',
                        data_action: 'public service button click',
                        data_name: '면허 승격하러 가기',
                      });
                      goToLicenseRenewal();
                    }}
                  >
                    {t('T10291')}
                  </button>
                </div>
              </li>
            );
          case GetRenewalEligibleResponse.renewalType.ACCIDENT_FREE_OVER_7YEARS_RENEWAL:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">
                    {t('T10222')}
                    <div className="badge_box">
                      <span className="badge badge_basic_container">{t('T10285')}</span>
                      <span className="badge badge_container_gray">{t('T10289')}</span>
                    </div>
                  </strong>
                </div>
                <p
                  className="desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T10292')}
                </p>
                <div className="btn_box">
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full"
                    onClick={() => {
                      emitEventLog({
                        event: 'public_service_interaction',
                        data_action: 'public service button click',
                        data_name: '면허 갱신하러 가기',
                      });
                      goToLicenseRenewal();
                    }}
                  >
                    {t('T10293')}
                  </button>
                </div>
              </li>
            );
          case GetRenewalEligibleResponse.renewalType.NOT_SUBJECT:
            return (
              <li className="application_item asset_after">
                <div className="title_area">
                  <strong className="title">{t('T10222')}</strong>
                </div>
                <div className="guide_box">
                  <p
                    className="text"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('T10225')}
                  </p>
                </div>
              </li>
            );
          default:
            return null;
        }
      })()}
    </>
  );
}

export default LicenseRenewalContents;
